import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useLoginIndorIDMutation } from "src/api/rtk/indorsoftRu/slices/indorIDAuthApiSlice";
import css from "./LoginIndorIDModal.module.scss";

const indorIdLogoSrc = require('../../assets/images/icons/indorId/indor_id_logo.svg').default;

type LoginIndorIDModalProps = {
    open: boolean;
    onClose: () => void;
    onLogin: () => void;
};

type LoginFormValues = {
    login: string;
    password: string;
    remember: boolean;
};

type LoginIndorIDFormProps = {
    onClose: () => void,
    onLogin: () => void
}

export default function LoginIndorIDModal({ open, onClose, onLogin }: LoginIndorIDModalProps) {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);

    function onOverlayClick(e: any) {
        if (e.target === modalRef.current) onClose();
    }

    if (!open) return null as any;

    return (
        <div ref={modalRef} className={css.overlay} onMouseDown={onOverlayClick}>
            <div className={css.modal} role="dialog" aria-modal="true" aria-labelledby="login-indorid-title">
                <div className={css.header}>
                    <div className={css.brand}>
                        <img src={indorIdLogoSrc} />
                        <span id="login-indorid-title" className={css.title}>Авторизоваться</span>
                    </div>
                    <button className={css.closeBtn} aria-label="Закрыть" onClick={onClose}>×</button>
                </div>

                <LoginIndorIDForm onClose={onClose} onLogin={onLogin} />

                <div className={css.footer}>
                    <span>Ещё нет аккаунта?</span>
                    <a
                        href="https://indorsoft.ru/auth/?register=yes&backurl=%2Findex.php"
                        target="_blank"
                        rel="noopener noreferrer"
                    >Создать Indor ID</a>
                </div>
            </div>
        </div>
    );
}



function LoginIndorIDForm({ onClose, onLogin }: LoginIndorIDFormProps) {
    const [loginIndorID, { isLoading, isSuccess, error }]: any = useLoginIndorIDMutation();
    const [serverErrorText, setServerErrorText] = useState<string | null>(null);

    const { register, handleSubmit, formState: { errors }, reset }: any = useForm<LoginFormValues>({
        defaultValues: { login: "", password: "", remember: false }
    });

    useEffect(() => {
        if (isSuccess) {
            reset();
            onLogin();
            onClose();
        }
    }, [isSuccess, reset, onClose]);

    useEffect(() => {
        if (error) {
            const status = error?.status
            let errorText = "Ошибка";
            if (status === "FETCH_ERROR") errorText = "Не удалось подключиться к IndorID"
            if (status === 504) errorText = "Ошибка сервера IndorID"
            if (status === 200) errorText = "Неправильный логин или пароль"
            setServerErrorText(errorText);
        } else {
            setServerErrorText(null);
        }
    }, [error]);

    async function onSubmit(values: LoginFormValues) {
        await loginIndorID({ login: values.login, password: values.password, remember: values.remember });
    }

    return (
        <form className={css.form} onSubmit={handleSubmit(onSubmit)}>
            <label className={css.label} htmlFor="indorid-login">Логин</label>
            <input
                id="indorid-login"
                type="text"
                placeholder="Введите логин"
                className={css.input}
                aria-invalid={!!errors["login"]}
                {...register("login", { required: "Введите логин." })}
            />
            {errors["login"] && <span className={css.error}>{errors["login"].message as string}</span>}

            <label className={css.label} htmlFor="indorid-password">Пароль</label>
            <input
                id="indorid-password"
                type="password"
                placeholder="Введите пароль"
                className={css.input}
                aria-invalid={!!errors["password"]}
                {...register("password", { required: "Введите пароль." })}
            />
            {errors["password"] && <span className={css.error}>{errors["password"].message as string}</span>}

            {serverErrorText && <span className={css.error}>{serverErrorText}</span>}

            <label className={`${css.checkbox} ${css.label}`}>
                <input type="checkbox" {...register("remember")} />
                <span>Запомнить меня</span>
            </label>

            <div className={css.actions}>
                <a
                    className={css.forgotLink}
                    href="https://indorsoft.ru/auth/?forgot_password=yes&backurl=%2Findex.php"
                    target="_blank"
                    rel="noopener noreferrer"
                >Забыли пароль?</a>
                <button type="submit" className={css.submitBtn} disabled={isLoading}>
                    {isLoading ? "Вход..." : "Войти"}
                </button>
            </div>
        </form>
    );
}


